import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { MessageSquare, Send, Trash2, Bot, User, AlertTriangle } from 'lucide-react';
import { LLMModelSelector } from '../selectors';
import { CompactSelect } from '../common/CompactSelect';
import type { CompactOption } from '../common/CompactSelect';
import { LoginModal } from '../auth';
import { TokenEstimateCard } from '../tokens/TokenEstimateCard';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { useGeoRestriction } from '@/hooks/useGeoRestriction';
import { isNsfwLlmModel } from '@/constants/geoRestriction';
import { URLService } from '@/services';
import { buildCloudApiUrl } from '@/services/urlResolver';
import { LLM_PROVIDERS } from '@/constants';
import { useLLMModels } from '@/hooks/useLLMModels';

import { handleError } from '../../utils/errorHandler';
import { extractResponseErrorMessage, getAuthHeadersFromStorage } from '../../utils/httpUtils';
import { isServerlessMode } from '../../utils/apiMode';
import {
  costGateBlockLabel,
  COST_GATE_OPEN,
  type CostGateStatus,
  type CostGate,
} from '../../utils/costGate';

/**
 * Output budget this tab asks POST /api/v1/chat/completion for.
 *
 * ONE symbol, because the cost card and the request must name the same
 * number: the route reserves `max_tokens`, so a card quoting a different
 * budget quotes a different price than the one charged. It matches the
 * backend default in constants/llm/config.py (CHAT_DEFAULT_MAX_TOKENS).
 */
const CHAT_MAX_TOKENS = 4096;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'warning';
  content: string;
  timestamp: Date;
  /**
   * Model that produced this assistant turn. Shown on the bubble, and sent back
   * to the API so the backend can tell the newly-selected model that earlier
   * turns came from a different one (otherwise it reads "I am Gemma" in the
   * history as its own words and keeps answering as Gemma).
   */
  model?: string;
}

interface ChatCompletionRequest {
  messages: Array<{ role: string; content: string; model?: string }>;
  model: string;
  provider: string;
  temperature: number;
  max_tokens: number;
}

export const ChatTab: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { isLoginModalOpen, loginMessage, closeLoginModal, requireAuth } = useRequireAuth();
  const { isFeatureAllowed } = useGeoRestriction();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // LLM Configuration
  const [llmProvider, setLlmProvider] = useState<string>('openrouter');
  const {
    availableModels,
    isLoadingModels,
    selectedModel: llmModel,
    setSelectedModel: setLlmModel,
  } = useLLMModels(llmProvider, undefined, { context: 'chat' });
  const [costGate, setCostGate] = useState<CostGateStatus>(COST_GATE_OPEN);

  // Token estimate parameters: ChatCompletionRequest's own fields, so the card
  // asks about the request it is about to send.
  //
  // `messages`, not a pre-computed `input_tokens`. That was a second name for
  // the prompt size which the request does not carry, and the two sides counted
  // it differently -- the card rounded each message UP to a token boundary
  // (sum of ceil(len/4)) while POST /api/v1/chat/completion charges on the whole
  // conversation at once (floor(total_chars/4)), so the card over-quoted by up
  // to one token per message. Sending the messages lets the price be computed
  // once, by the same code that charges.
  const estimateParams = useMemo(() => {
    const priceableMessages = messages
      .filter((msg) => msg.role !== 'warning')
      .map((msg) => ({ role: msg.role, content: msg.content }));
    if (inputText) {
      priceableMessages.push({ role: 'user', content: inputText });
    }

    return {
      model: llmModel,
      provider: llmProvider,
      messages: priceableMessages,
      // The SAME budget the send below asks for, because that is what the
      // route reserves (shared/routes/chat.py bills chat_request.max_tokens).
      // This used to be a second field name, output_tokens, carrying 1024
      // while the send asked for 4096 — the card quoted a quarter of the charge.
      max_tokens: CHAT_MAX_TOKENS,
    };
  }, [messages, inputText, llmModel, llmProvider]);

  // Handle token estimate change
  const handleEstimateChange = useCallback(({ status }: CostGate) => {
    setCostGate(status);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message
  const handleSendMessage = useCallback(async () => {
    if (!inputText.trim() || isLoading) return;

    // Require authentication before sending message
    requireAuth(async () => {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: inputText.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInputText('');
      setIsLoading(true);

      try {
        // Cloud LLM providers (openrouter, runpod) MUST hit Cloud Run so the
        // server-side pre-charge/settle charges tokens — they run on the
        // project's API key and are never free. Routing them to the local desktop
        // server bypassed billing entirely (revenue leak). Only ollama is a true
        // local provider and stays on the local server (free).
        const isCloudLlmProvider = llmProvider !== 'ollama';
        const completionUrl = isCloudLlmProvider
          ? buildCloudApiUrl('/api/v1/chat/completion')
          : `${URLService.getAPIBaseUrl()}/api/v1/chat/completion`;
        const headers = await getAuthHeadersFromStorage();

        // Build messages for API (include conversation history, exclude warning messages).
        // Assistant turns carry the model that wrote them so the backend can guard
        // against the previous model's identity bleeding into the new one.
        const apiMessages = messages
          .filter((msg) => msg.role !== 'warning')
          .map((msg) => ({
            role: msg.role,
            content: msg.content,
            ...(msg.model ? { model: msg.model } : {}),
          }));
        apiMessages.push({ role: 'user', content: userMessage.content });

        const request: ChatCompletionRequest = {
          messages: apiMessages,
          model: llmModel,
          provider: llmProvider,
          temperature: 0.7,
          max_tokens: CHAT_MAX_TOKENS,
        };

        const response = await fetch(completionUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          // Handle model unavailable (404)
          if (response.status === 404) {
            const warningMessage: ChatMessage = {
              id: `warning-${Date.now()}`,
              role: 'warning',
              content: t('chat.errors.modelUnavailableInChat', {
                defaultValue: 'Model not available right now, please try another one.',
              }),
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, warningMessage]);
            return;
          }

          // Extract error message from response. Every backend failure --
          // including the 401/403/415 that middleware answers before any route
          // runs -- carries its prose in error.message of the shared envelope,
          // so the ONE extractor in httpUtils is what reads it (RULE #0).
          let errorMessage = `Request failed with status ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = extractResponseErrorMessage(errorData) ?? errorMessage;
          } catch {
            // Response is not valid JSON, use default message
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        // Single source of truth: shared/routes/chat.py
        // Format: {id, model, provider, message: {role, content}, usage, finish_reason}
        const content = data.message?.content;
        if (content) {
          const assistantMessage: ChatMessage = {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content,
            timestamp: new Date(),
            // data.model is the model that SERVED the request, which is not
            // always the one selected: OpenRouter cascades to a fallback when
            // the chosen model is rate-limited.
            model: data.model || llmModel,
          };

          setMessages((prev) => [...prev, assistantMessage]);

          // Handle model fallback: update selector + show inline warning
          if (data.model && data.model !== llmModel) {
            setLlmModel(data.model);

            const fallbackWarning: ChatMessage = {
              id: `warning-fallback-${Date.now()}`,
              role: 'warning',
              content: t('chat.notifications.modelFallbackInline', {
                defaultValue:
                  'Rate limit reached for {{original}}. Switched to {{model}} automatically.',
                original: llmModel,
                model: data.model,
              }),
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, fallbackWarning]);

            showToast(
              'info',
              t('chat.notifications.modelFallback', { defaultValue: 'Model Fallback' }),
              t('chat.notifications.modelFallbackMessage', {
                defaultValue: 'Using {{model}} due to rate limiting',
                model: data.model,
              })
            );
          }
        } else {
          throw new Error('No content in response');
        }
      } catch (error) {
        const errorMessage = handleError(error, 'ChatTab', 'Unknown error');
        showToast(
          'error',
          t('chat.errors.failedTitle', { defaultValue: 'Chat Error' }),
          errorMessage
        );
      } finally {
        setIsLoading(false);
      }
    }, t('auth.loginRequired.chat'));
  }, [
    inputText,
    isLoading,
    messages,
    llmModel,
    llmProvider,
    setLlmModel,
    showToast,
    t,
    requireAuth,
  ]);

  // Handle enter key
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  // Clear conversation
  const handleClearChat = useCallback(() => {
    setMessages([]);
    showToast(
      'info',
      t('chat.notifications.cleared', { defaultValue: 'Chat Cleared' }),
      t('chat.notifications.clearedMessage', { defaultValue: 'Conversation has been cleared' })
    );
  }, [showToast, t]);

  // Resolve a model id to the label used in the selector, so the bubble names the
  // model the same way the picker does. Falls back to the raw id for a model that
  // is no longer in the list (e.g. a cascade fallback the selector never offered).
  const getModelLabel = useCallback(
    (modelId: string): string => availableModels.find((m) => m.value === modelId)?.label ?? modelId,
    [availableModels]
  );

  // Convert LLM_PROVIDERS to CompactOption format
  const llmProviderOptions: CompactOption[] = LLM_PROVIDERS.map((provider) => ({
    value: provider.value,
    label: provider.label,
    disabled: provider.disabled,
  }));

  return (
    <div className="max-w-4xl mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 py-4 xs:py-6 sm:py-8 min-h-[calc(100vh-100px)] xs:min-h-[calc(100vh-120px)] sm:min-h-[calc(100vh-140px)] flex flex-col">
      {/* Header */}
      <div className="text-center mb-2 xs:mb-3">
        <div className="inline-flex items-center justify-center w-10 h-10 xs:w-12 xs:h-12 bg-gradient-to-r from-green-600 to-teal-600 rounded-xl mb-1 xs:mb-2">
          <MessageSquare className="h-5 w-5 xs:h-6 xs:w-6 text-white" />
        </div>
        <h2 className="text-lg xs:text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {t('chat.title', { defaultValue: 'AI Chat' })}
        </h2>
        <p className="text-xs xs:text-sm text-gray-600 dark:text-gray-400">
          {t('chat.subtitle', { defaultValue: 'Talk with any AI in the world' })}
        </p>
      </div>

      {/* Model Selection */}
      <div className="bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700 p-3 xs:p-4 mb-3 xs:mb-4">
        <div className="space-y-3 xs:space-y-4">
          {/* Provider Selector */}
          <CompactSelect
            label={t('chat.fields.provider', { defaultValue: 'LLM Provider' })}
            options={llmProviderOptions}
            value={llmProvider}
            onChange={setLlmProvider}
            disabled={isLoading}
            testId="chat-provider-select"
          />

          {/* Visual Model Selector with Avatars */}
          <LLMModelSelector
            models={
              isLoadingModels
                ? [{ value: '', label: 'Loading models...', disabled: true }]
                : availableModels.length === 0
                  ? [{ value: '', label: 'No models available', disabled: true }]
                  : (() => {
                      let filtered = availableModels;
                      if (!isFeatureAllowed('nsfw_models')) {
                        filtered = filtered.filter((m) => !isNsfwLlmModel(m.value));
                      }
                      return filtered;
                    })()
            }
            value={llmModel}
            onChange={setLlmModel}
            disabled={isLoading || isLoadingModels || availableModels.length === 0}
            label={t('chat.fields.model', { defaultValue: 'AI Model' })}
            showFreeOnly={false}
            hideFreeFilter={isServerlessMode()}
          />
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 min-h-[200px] bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700 overflow-hidden flex flex-col">
        {/* Messages Container */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 xs:p-4 space-y-3 xs:space-y-4">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <div className="text-center px-4">
                <MessageSquare className="h-8 w-8 xs:h-10 xs:w-10 mx-auto mb-1 xs:mb-2 opacity-50" />
                <p className="text-sm xs:text-base">
                  {t('chat.emptyState', { defaultValue: 'Start a conversation...' })}
                </p>
                <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
                  {t('chat.emptyStateHint', {
                    defaultValue: 'Type a message below to begin chatting with the AI',
                  })}
                </p>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[90%] xs:max-w-[80%] rounded-lg p-2.5 xs:p-3 ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : message.role === 'warning'
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-700'
                        : 'bg-gray-100 dark:bg-secondary-700 text-gray-900 dark:text-gray-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5 xs:space-x-2 mb-1">
                    {message.role === 'user' ? (
                      <User className="h-3.5 w-3.5 xs:h-4 xs:w-4" />
                    ) : message.role === 'warning' ? (
                      <AlertTriangle className="h-3.5 w-3.5 xs:h-4 xs:w-4 text-amber-600 dark:text-amber-400" />
                    ) : (
                      <Bot className="h-3.5 w-3.5 xs:h-4 xs:w-4" />
                    )}
                    <span className="text-[10px] xs:text-xs opacity-75">
                      {message.role === 'user'
                        ? t('chat.you', { defaultValue: 'You' })
                        : message.role === 'warning'
                          ? t('chat.warning', { defaultValue: 'Warning' })
                          : t('chat.assistant', { defaultValue: 'Assistant' })}
                    </span>
                    {message.role === 'assistant' && message.model && (
                      <span
                        className="text-[10px] xs:text-xs font-medium px-1.5 py-0.5 rounded bg-gray-200 dark:bg-secondary-600 text-gray-700 dark:text-gray-200 truncate max-w-[140px] xs:max-w-[220px]"
                        title={message.model}
                      >
                        {getModelLabel(message.model)}
                      </span>
                    )}
                  </div>
                  <div className="whitespace-pre-wrap break-words text-sm xs:text-base">
                    {message.content}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-secondary-700 rounded-lg p-2.5 xs:p-3">
                <div className="flex items-center space-x-2">
                  <Bot className="h-3.5 w-3.5 xs:h-4 xs:w-4 text-gray-600 dark:text-gray-400" />
                  <div className="flex space-x-1.5 items-end">
                    <div className="w-1.5 h-1.5 xs:w-2 xs:h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-typing-dot" />
                    <div
                      className="w-1.5 h-1.5 xs:w-2 xs:h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-typing-dot"
                      style={{ animationDelay: '0.2s' }}
                    />
                    <div
                      className="w-1.5 h-1.5 xs:w-2 xs:h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-typing-dot"
                      style={{ animationDelay: '0.4s' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 dark:border-secondary-700 p-2.5 xs:p-4 space-y-2">
          {/* Token Estimate - show when there's input text */}
          {inputText.trim().length > 0 && (
            <TokenEstimateCard
              operationPath="/api/v1/chat/completion"
              parameters={estimateParams}
              onEstimateChange={handleEstimateChange}
            />
          )}

          <div className="flex space-x-1.5 xs:space-x-2">
            <button
              onClick={handleClearChat}
              disabled={messages.length === 0 || isLoading}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              title={t('chat.buttons.clear', { defaultValue: 'Clear chat' })}
            >
              <Trash2 className="h-4 w-4 xs:h-5 xs:w-5" />
            </button>
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('chat.inputPlaceholder', {
                defaultValue: 'Type your message...',
              })}
              disabled={isLoading || !llmModel}
              rows={1}
              className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white px-3 xs:px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-secondary-800 disabled:cursor-not-allowed text-sm xs:text-base"
              style={{ minHeight: '42px', maxHeight: '120px' }}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputText.trim() || isLoading || !llmModel || costGate !== 'affordable'}
              className="px-3 xs:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 xs:space-x-2 flex-shrink-0"
            >
              <Send className="h-4 w-4 xs:h-5 xs:w-5" />
              <span className="hidden sm:inline">
                {costGateBlockLabel(costGate, t) ??
                  t('chat.buttons.send', { defaultValue: 'Send' })}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Login Modal (auth required for actions) */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} message={loginMessage} />
    </div>
  );
};
